import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Navigate, useLocation } from 'react-router'
import { clearExpressJointApplicantDraft } from '@/lib/express-joint-applicant-draft'
import { clearExpressPrefillPayload } from '@/lib/express-prefill'

export interface AuthUser {
  id: string
  email: string
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

interface AuthContextValue {
  status: AuthStatus
  user: AuthUser | null
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface MeResponse {
  ok?: boolean
  data?: {
    user?: { id?: unknown; email?: unknown }
  }
}

async function fetchMe(signal?: AbortSignal): Promise<AuthUser | null> {
  try {
    const response = await fetch('/api/auth/me', {
      credentials: 'include',
      cache: 'no-store',
      signal,
    })
    if (!response.ok) { return null }
    const json = (await response.json().catch(() => null)) as MeResponse | null
    if (!json?.ok) { return null }
    const user = json.data?.user
    if (!user) { return null }
    const id = typeof user.id === 'string' ? user.id : ''
    const email = typeof user.email === 'string' ? user.email : ''
    if (!id || !email) { return null }
    return { id, email }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<AuthUser | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const applyResult = useCallback((result: AuthUser | null) => {
    if (!mountedRef.current) { return }
    if (result) {
      setUser(result)
      setStatus('authenticated')
    } else {
      setUser(null)
      setStatus('unauthenticated')
    }
  }, [])

  const refresh = useCallback(async () => {
    const result = await fetchMe()
    applyResult(result)
  }, [applyResult])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      })
    } catch {
      // Even if the network call fails, clear local state so UI reflects sign-out.
    }
    try {
      clearExpressPrefillPayload()
      clearExpressJointApplicantDraft()
      window.sessionStorage.removeItem('cao_apply_source')
      window.sessionStorage.removeItem('bao_application_id')
      window.sessionStorage.removeItem('bao_dashboard_invite_sent')
      window.sessionStorage.removeItem('bao_account_selection')
      window.sessionStorage.removeItem('dao_cart')
      window.sessionStorage.removeItem('dao_selected_accounts')
      window.sessionStorage.removeItem('gbc_cart')
      window.sessionStorage.removeItem('gbc_selected_accounts')
    } catch {
      // Ignore storage cleanup failures.
    }
    if (mountedRef.current) {
      setUser(null)
      setStatus('unauthenticated')
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void fetchMe(controller.signal).then((result) => {
      applyResult(result)
    })
    return () => {
      controller.abort()
    }
  }, [applyResult])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, refresh, logout }),
    [status, user, refresh, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}

interface ProtectedRouteProps {
  children: ReactNode
  redirectTo?: string
  fallback?: ReactNode
}

function isSafePath(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) { return false }
  if (/^[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(path)) { return false }
  return true
}

export function ProtectedRoute({
  children,
  redirectTo = '/login',
  fallback = null,
}: ProtectedRouteProps) {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return <>{fallback}</>
  }

  if (status === 'unauthenticated') {
    const nextPath = `${location.pathname}${location.search}`
    const safeNext = isSafePath(nextPath) ? nextPath : '/'
    const target = `${redirectTo}?next=${encodeURIComponent(safeNext)}`
    return <Navigate to={target} replace />
  }

  return <>{children}</>
}
